import fm from 'format-message';
import PropTypes from 'prop-types';
import { getBreakpoint } from '@activebrands/core-web/config/breakpoints';
import useNotifications from '@activebrands/core-web/hooks/useNotifications';
import { OVERLAY_ID } from '@activebrands/core-web/config/overlay';
import overlay from '@activebrands/core-web/libs/overlay';
import { styled } from '@activebrands/core-web/libs/styletron';
import { addBasketItem } from '@activebrands/core-web/state/basket/actions';
import { useTypedDispatch } from '@activebrands/core-web/state/store';
import { logger } from '@activebrands/core-web/utils/logger';
import ThemeButton from 'components/buttons/ThemeButton';
import { sizes } from 'components/buttons/ThemeButton/theme-button-config';
import AddToBasketIcon from 'components/icons/AddToBasketIcon';
import LoadingIcon from 'components/icons/LoadingIcon';
import StateIcon from 'components/icons/StateIcon';
import AddedToBasket from 'components/notifications/AddedToBasket';
import Paragraph from 'components/text/Paragraph';

export const Wrapper = styled('div', {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'end',
});

// @todo: add onclick for adding to wishlist
const AddToBasketCardEnd = ({
    $style,
    addedToBasketAlgoliaData,
    addedToBasketData,
    color,
    isLoading = false,
    isQuickshopOpen,
    name,
    priceWithCurrency,
    salePriceWithCurrency,
    setIsLoading = () => null,
    setIsQuickshopOpen = () => null,
    uri = '',
    variations = [],
}) => {
    const dispatch = useTypedDispatch();
    const notifications = useNotifications();
    const isOnlyOneVariant = variations.length === 1;
    let Icon;
    let buttonLabel;

    if (isQuickshopOpen) {
        Icon = StateIcon;
        buttonLabel = fm('Close');
    } else {
        Icon = AddToBasketIcon;
        buttonLabel = fm('Add to bag');
    }

    if (isLoading) {
        Icon = LoadingIcon;
        buttonLabel = fm('Adding to bag');
    }

    const handleAddToBasket = async variation => {
        setIsLoading(true);
        try {
            const response = await dispatch(addBasketItem(variation.id, variation.trackingMeta));
            if (response.status === 200 || response.status === 201) {
                notifications.push(AddedToBasket, addedToBasketData, 4500);
            }
        } catch (error) {
            logger.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleQuickShop = variation => {
        setIsQuickshopOpen(!isQuickshopOpen);

        const breakpoint = getBreakpoint('mobile.lg');

        if (isOnlyOneVariant) {
            handleAddToBasket(variation);
        } else if (breakpoint.to > window?.innerWidth) {
            overlay.open(OVERLAY_ID.QUICKSHOP, {
                activePrice: salePriceWithCurrency,
                addedToBasketAlgoliaData,
                addedToBasketData,
                color,
                name,
                originalPrice: priceWithCurrency,
                uri,
                variations,
            });
        }
    };

    return (
        <Wrapper $style={$style}>
            <ThemeButton
                $style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: ['center', null, null, 'initial'],
                    gap: '8px',
                    padding: [0, null, null, sizes.md.padding],
                    height: ['40px', null, null, 'auto'],
                    width: ['40px', null, null, 'auto'],
                }}
                type="button"
                aria-label={
                    isOnlyOneVariant
                        ? fm('Add {product} to cart', { product: name })
                        : fm('Quick add {product} to cart', { product: name })
                }
                onClick={() => handleQuickShop(variations[0])}
            >
                <Paragraph
                    $style={{ display: ['none', null, null, 'initial'] }}
                    fontKeys="Miscellaneous/14_100"
                    aria-hidden="true"
                >
                    {buttonLabel}
                </Paragraph>
                <Icon size="16px" state="close" />
            </ThemeButton>
        </Wrapper>
    );
};

AddToBasketCardEnd.propTypes = {
    $style: PropTypes.object,
    addedToBasketAlgoliaData: PropTypes.object,
    addedToBasketData: PropTypes.object,
    color: PropTypes.string,
    isLoading: PropTypes.bool,
    isQuickshopOpen: PropTypes.bool,
    name: PropTypes.string,
    priceWithCurrency: PropTypes.string,
    salePriceWithCurrency: PropTypes.string,
    setIsLoading: PropTypes.func,
    setIsQuickshopOpen: PropTypes.func,
    uri: PropTypes.string,
    variations: PropTypes.array,
};

export default AddToBasketCardEnd;
